// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/jobs/model/rx_build.py,
// backend/app/jobs/model/rx.py and backend/app/jobs/model/fert_rx.py.
// Change both and update the parity fixtures in shared/vectors/rx_presets.json.
// Why: the public API builds Rx on the server (ADR 0003 and ADR 0009 amendments).
// CI runs the same fixtures in frontend/src/lib/rx.vectors.test.ts and
// backend/worker_tests/test_rx_vectors.py. A change to one side alone fails CI.
// ============================================================================
import { api } from "../api";
import { layersFromDocument } from "./layerLoad";
import { mockPropertyValues } from "./data/syntheticGrid";
import { sampleCogAtPoints } from "./cogSampler";
import {
  CLAY,
  DIFF_K,
  DIFF_P,
  IDW_CA,
  IDW_MG,
  MAP_UNIT,
  covariateToggle,
  gridNumbers,
  gridStamp,
  gridValues,
  joinForGrid,
  kFixEfficiencyOnGrid,
  loadRxCells,
  loadRxInputs,
  type DisabledCovariateToggle,
  type RxJoin,
} from "./rxGrid";
import {
  assembleFertLayers,
  cationInputsAvailable,
  DEFAULT_ALBRECHT_TARGETS,
  DEFAULT_CATION_RATIOS,
  withLimeSoilStamp,
  type RecInputs,
} from "./fertility";
import { idwAnalyte, type KrigePoint } from "./kriging";
import { lbsPerPpmAt } from "./soilUnits";
import { clayGroupsInField } from "./soilComposition";
import { perCellPMinCredit } from "./pMineralization";
import { haneyFromWire, haneyParamsWithData, type HaneyPoint } from "./haney";
import { loadLocalHaney } from "../stores/haneyStore";
import {
  CROP_REMOVAL,
  DEFAULT_YIELD,
  type Replacement,
  type Season,
  simYieldKeysByCrop,
  yieldOptionsFromCovariates,
  meanYieldMaps,
  type YieldMaps,
  type SeasonPlan,
  resolveSeasons,
  replacementForSeasons,
  DEFAULT_SEASON_CROPS,
} from "./replacement";
import { commitRecSet, type SavedRecSet } from "./fertSets";
import { useFertRecs } from "../stores/fertRecsStore";
import { useLayerStore } from "../stores/layerStore";
import type {
  DashboardField,
  FieldCovariate,
  FieldSoilComposition,
  ManifestLayer,
  SoilSamplePoint,
} from "../types/contract";

// "Build Rx" opens three side-by-side preset menus (Lime / P / K); picking ONE preset launches
// immediately: a seasons/crops/yield pop-up, then this module computes + saves a rec for that ONE
// nutrient with the preset's exact rec-config flag combo.

/*
 *
 * A plain constant on purpose: it is only ever compared against `feature_manifest_version` to
 * produce a sentence, never to gate behaviour. If it goes stale the message says "rebuild" a
 * version late — annoying never wrong — whereas gating on it would break fields the day compute
 * ships a new manifest.
 */
export const LATEST_MANIFEST = "round01_v21";

export type Nutrient = "lime" | "p2o5" | "k2o";
export interface PresetFlags {
  limeAlbrecht?: boolean;
  k2oAlbrecht?: boolean;
  cation?: boolean;
  kFix?: boolean;
  pMin?: boolean;
  replP?: boolean;
  replK?: boolean;
  useDiffusion?: boolean; // use the diffusion sample-corrected P/K supply map instead of ml-p/ml-k.
}

export interface FertPreset {
  id: string;
  nutrient: Nutrient;
  label: string;
  desc: string; // the short (i) tooltip blurb (always shown)
  compareTo?: string; // "Compare to: …" line shown prominently at the top of the (i) box (v2 descriptions)
  descUnderline?: string[]; // substrings of `desc` to underline (the Genius factor phrases → the expand breakdown)
  details?: string[]; // optional "^^ Expand ^^" breakdown revealed by the tooltip's Expand toggle
  flags: PresetFlags;
  /*
   *
   * Per-PRESET, not a platform constant only the Genius recs carry one, because only they claim to
   * be a finished spreading plan rather than a pure agronomic buildup.
   */
  rateBounds?: { min: number; max: number; dropBelow?: number };
  custom?: boolean;
}

export const LIME_PRESETS: FertPreset[] = [
  {
    id: "lime-traditional",
    nutrient: "lime",
    label: "Traditional",
    desc: "Tried & true. Calculates the rate to bring the field up to a certain pH level (default is 6.5).",
    compareTo: "University Rx",
    flags: {},
  },
  {
    id: "lime-albrecht",
    nutrient: "lime",
    label: "Soil Balance (Albrecht)",
    desc: "Unconventional method pioneered by Dr. William A. Albrecht & popularized by Brookside Labs. Limes toward a target calcium base saturation (default 65%), not a pH level.",
    compareTo: "Brookside Network",
    flags: { limeAlbrecht: true },
  },
  // NO 'lime-custom' card: the in-map Rx editor REPLACED the a-la-carte factor picker for lime.
];
export const P_PRESETS: FertPreset[] = [
  {
    id: "p-genius",
    nutrient: "p2o5",
    label: "Genius Rx",
    desc: "A Next-Gen P Rec, adapting the university critical level method to account for sub-field variations in soil biology, moisture/diffusion rates, and crop removal — all based on your field’s real data.",
    compareTo: "Baseline Rx + Advanced Agrilytics",
    descUnderline: ["soil biology", "moisture/diffusion rates", "crop removal"],
    details: [
      "#1 University Critical Levels: Calculates the minimum phosphate required to grow a healthy crop.",
      "#2 Biological Modifier: Uses a Haney test (WEOC, WEON, & CO₂ respiration) to calculate the P₂O₅ that you will get FOR FREE from your soil’s biology.",
      "#3 Moisture/Diffusion Modifier: Uses “estimated diffused phosphate” (calculated from Lab P/OM/CEC, topography, & government data) instead of lab-extractable phosphorus.",
      "#4 Variable Crop Removal, From Real Yield Data: Uses all the yield data you can give us to calculate the P₂O₅ replacement rate in each area.",
    ],
    flags: { pMin: true, replP: true, useDiffusion: true },
    rateBounds: { min: 25, max: 350 },
  },
  {
    id: "p-soil-health",
    nutrient: "p2o5",
    label: "Basic + Soil Health Mod",
    desc: "A very popular rec, adapting the university critical level method to account for what your soil biology gives you for FREE.",
    compareTo: "Baseline Rx",
    details: [
      "Soil biology mineralizes some organic P (not measured in the lab test P) throughout the season.",
      "A “Haney Test” estimates how much additional P will be mineralized using WEOC, WEON, and carbon dioxide respiration.",
      "The natural, mineralized P is then subtracted from your rec.",
    ],
    flags: { replP: true, pMin: true },
  },
  {
    id: "p-field-conditions",
    nutrient: "p2o5",
    label: "Basic + Field Conditions Mod",
    desc: "A creative but conventional rec that builds to the university critical level and replaces what the crop removes.",
    compareTo: "Advanced Agrilytics",
    flags: { replP: true, useDiffusion: true },
  }, // the moisture/diffusion modifier is what makes this "Field Conditions" — without it this preset computed.
  {
    id: "p-basic",
    nutrient: "p2o5",
    label: "Basic Build + Maintain",
    desc: "Tried & true. Calculates the rate to bring the field up to the university critical level and replace what the crop is taking. Uses yield data to calculate removal if it’s available, but otherwise lets you choose a “yield goal” replacement rate.",
    compareTo: "University Rx",
    flags: { replP: true },
  },
];
export const K_PRESETS: FertPreset[] = [
  {
    id: "k-genius",
    nutrient: "k2o",
    label: "Genius Rx",
    desc: "A Next-Gen K Rec, adapting the university critical level method to account for sub-field variations in moisture/diffusion rates, clay fixation, cation balances, and crop removal — all based on your field’s real data.",
    compareTo: "Advanced Agrilytics",
    descUnderline: [
      "moisture/diffusion rates",
      "clay fixation",
      "cation balances",
      "crop removal",
    ],
    details: [
      "#1 University Critical Levels: Calculates the minimum K₂O required to grow a healthy crop.",
      "#2 Moisture/Diffusion Modifier: Uses “estimated diffused K₂O” (calculated from Lab K/OM/CEC, topography, & government data) instead of lab-extractable K.",
      "#3 Clay Fixation Modifier: Uses government data and university research to estimate what portion of lab-extractable K will be “fixed” in the soil due to different clay mineralogy types.",
      "#4 Cation Balancing: Ensures target levels are within a threshold Mg:K ratio to avoid nutrient tie-up.",
      "#5 Variable Crop Removal, From Real Yield Data: Uses all the yield data you can give us to calculate the K₂O replacement rate in each area.",
    ],
    flags: { cation: true, kFix: true, replK: true, useDiffusion: true },
    rateBounds: { min: 25, max: 500 },
  },
  {
    id: "k-basic",
    nutrient: "k2o",
    label: "Basic Build + Maintain",
    desc: "Tried & true. Calculates the rate to bring the field up to the university critical level and replace what the crop is taking. Uses yield data to calculate removal if it’s available, but otherwise lets you choose a “yield goal” replacement rate.",
    compareTo: "University Rx",
    flags: { replK: true },
  },
  {
    id: "k-albrecht",
    nutrient: "k2o",
    label: "Soil Balance (Albrecht)",
    desc: "Unconventional method pioneered by Dr. William A. Albrecht & popularized by Brookside Labs. Builds K to a certain target saturation, not to the critical level.",
    compareTo: "Brookside Network",
    flags: { k2oAlbrecht: true },
  },
];

// K NO LONGER DOES: every K preset runs the fixed corn+soybean rotation with each season's basis
// resolved from the data the field actually has (machine → simulated → per-crop flat), so there is
// nothing left to ask.
export function presetNeedsSeasons(_p: FertPreset): boolean {
  // NOTHING does any more: P joined K on the fixed corn+soybean rotation with each season's basis
  // resolved from the data the field actually has, so there is nothing left to ask before a run.
  return false;
}

const NUTRIENT_NEEDS: Record<Nutrient, string[]> = {
  lime: ["ph", "cec"],
  p2o5: ["p"],
  k2o: ["k", "cec"],
};
const BASE_PROPS = ["ph", "p", "k", "cec"];
const num = (v: number | null | undefined): v is number =>
  v != null && Number.isFinite(v);

function candidatesFor(
  prop: string,
  manifest: ManifestLayer[],
): ManifestLayer[] {
  return manifest.filter(
    (l) => l.id === `ml-${prop}` || (prop === "ph" && l.id === "ml-map"),
  );
}

function nutrientPts(
  samples: SoilSamplePoint[] | undefined,
  key: string,
): KrigePoint[] {
  return (samples ?? [])
    .map((s) => {
      // `properties_8in` is ALWAYS ppm (compute normalizes units first). The MEASURED fallback is
      // the as-uploaded bag — on a `units='lbs_ac'` row those are POUNDS, and handing them onward
      // as ppm is byte-for-byte the bug that zeroed served Albrecht surface (compute's rx.
      const norm = s.properties_8in?.[key];
      if (typeof norm === "number" && Number.isFinite(norm))
        return { lng: s.lon, lat: s.lat, value: norm };
      let v = s.properties?.[key] as number;
      if (s.units === "lbs_ac" && Number.isFinite(v)) {
        // convert at the ROW's own depth; without a depth the row is unconvertible — drop it, never guess
        v =
          Number.isFinite(s.depth_in) && s.depth_in > 0
            ? v / lbsPerPpmAt(s.depth_in)
            : NaN;
      }
      return { lng: s.lon, lat: s.lat, value: v };
    })
    .filter((p) => Number.isFinite(p.value));
}

async function signedUrlFor(
  fieldId: string,
  layerId: string,
  manifest: ManifestLayer[],
): Promise<string | undefined> {
  const row = manifest.find((layer) => layer.id === layerId);
  if (row?.signed_url) return row.signed_url;
  try {
    return (await api.fetchLayerDetail(fieldId, layerId)).signed_url;
  } catch {
    return undefined;
  }
}

async function sampleSoil(
  fieldId: string,
  centroids: { lng: number; lat: number }[],
  props: string[],
  sources: Record<string, string>,
  manifest: ManifestLayer[],
): Promise<RecInputs> {
  const out: Record<string, (number | null)[]> = {};
  if (import.meta.env.MODE === "test") {
    for (const p of props) out[p] = mockPropertyValues(centroids, p);
  } else {
    await Promise.all(
      props.map(async (p) => {
        const url = await signedUrlFor(fieldId, sources[p], manifest);
        out[p] = url
          ? await sampleCogAtPoints(url, centroids)
          : centroids.map(() => null);
      }),
    );
  }
  // An unsampled core property is all-null, one per cell, never []. The server refuses a save
  // whose soil carries a list that is not cellCount long ("A per-cell array does not match cellCount.").
  const column = (p: string) => out[p] ?? centroids.map(() => null);
  const soil: RecInputs = {
    ph: column("ph"),
    p: column("p"),
    k: column("k"),
    cec: column("cec"),
  };
  if (out.ca) soil.ca = out.ca;
  if (out.mg) soil.mg = out.mg;
  if (out.bph) soil.bph = out.bph;
  return soil;
}

// Present only once the field has samples + an ML map (Way-2 chains after model. Genius P/K use
// these instead of the ml maps; the custom P/K panels expose an opt-in "Use diffusion models"
// toggle.
export function diffusionPKKeys(covs: FieldCovariate[]): {
  p?: string;
  k?: string;
} {
  const resolve = (n: "p" | "k"): string | undefined => {
    const N = n === "p" ? "P" : "K";
    // `variance` was always excluded: variance layers live in the SAME `supply`
    // category and would otherwise satisfy the loose fallback below.
    const cand = covs.filter(
      (c) =>
        c.category === "supply" &&
        c.has_values !== false &&
        !/variance/i.test(`${c.title ?? ""} ${c.key}`),
    );
    // MUST be the SAMPLE-CORRECTED (Way-2, real Ca/Mg) effective — NOT the assumed-Ca/Mg (Way-1) effective, whose
    // title "P (diffusion, assumed Ca/Mg)" would otherwise match too. So every fallback is corr-specific.
    return (
      cand.find((c) => c.key === `diffusion__corr_${n}_effective`)?.key ?? // canonical §1C.7
      cand.find((c) =>
        new RegExp(`^\\s*${N}\\b.*diffusion.*sample.?corrected`, "i").test(
          c.title ?? "",
        ),
      )?.key ?? // "P (diffusion, sample-corrected)"
      cand.find((c) =>
        new RegExp(
          `^diffusion__corr_(${n}_effective|effective_${n}|${n})$`,
          "i",
        ).test(c.key),
      )?.key ?? // corr key variants
      cand.find(
        (c) =>
          /diffusion/i.test(c.title ?? "") &&
          !/assumed/i.test(c.title ?? "") &&
          new RegExp(`(^|[^a-z])${N}([^a-z]|$)`).test(c.title ?? ""),
      )?.key // loose, never assumed
    );
  };
  return { p: resolve("p"), k: resolve("k") };
}

// Which diffusion supply maps exist on this field (Way-2 only; gate before offering/using them).
export function diffusionPKAvailable(covs: FieldCovariate[]): {
  p: boolean;
  k: boolean;
} {
  const keys = diffusionPKKeys(covs);
  return { p: !!keys.p, k: !!keys.k };
}

// Read the ppm diffusion P/K columns from /rx-inputs (grid order, joined on cell_id).
export async function sampleDiffusionPK(
  fieldId: string,
  centroids: { lng: number; lat: number }[],
  keys: { p?: string; k?: string },
): Promise<{ p?: (number | null)[]; k?: (number | null)[] }> {
  const cols = [keys.p, keys.k].filter(Boolean) as string[];
  if (!cols.length) return {};
  const join = await loadRxInputs(fieldId, cols);
  const n = centroids.length;
  return {
    p: keys.p ? (gridNumbers(join, keys.p, n) ?? undefined) : undefined,
    k: keys.k ? (gridNumbers(join, keys.k, n) ?? undefined) : undefined,
  };
}

// Field-average yield (bu/ac) per crop from the JD yield-history columns, for the "use yield history"
// goal option in the seasons pop-up. Returns {} when the field has no usable yield years.
export async function cropYieldAverages(
  fieldId: string,
  yieldOpts: { key: string; crop: string; year: number }[],
): Promise<Record<string, number>> {
  if (yieldOpts.length === 0) return {};
  const byCrop: Record<string, number[]> = {};
  const cells = await loadRxCells(fieldId);
  const join = await loadRxInputs(
    fieldId,
    yieldOpts.map((o) => o.key),
  );
  const aligned = joinForGrid(
    cells,
    join,
    yieldOpts.map((o) => o.key),
  );
  const cols: Record<string, (number | null)[]> = {};
  for (const o of yieldOpts) {
    const column = gridNumbers(aligned, o.key, cells.cellCount);
    if (column) cols[o.key] = column;
  }
  for (const o of yieldOpts) {
    const vals = (cols[o.key] ?? []).filter(num);
    if (!vals.length) continue;
    const avg = vals.reduce((s, x) => s + x, 0) / vals.length;
    (byCrop[o.crop] ??= []).push(avg);
  }
  const out: Record<string, number> = {};
  for (const [crop, avgs] of Object.entries(byCrop))
    out[crop] = Math.round(avgs.reduce((s, x) => s + x, 0) / avgs.length);
  return out;
}

export interface PresetInputs {
  field: DashboardField;
  manifest: ManifestLayer[];
  samples: SoilSamplePoint[];
  covs: FieldCovariate[];
  comp: FieldSoilComposition | null;
  haneyPoints: HaneyPoint[];
}

/*
 * One field's preset inputs fetched directly — the Build Rx panel's hooks make the same reads.
 * Used by batch builds and by the wizard driver when it opens an Rx session without the panel.
 */
export async function fetchPresetInputsFor(
  f: DashboardField,
): Promise<PresetInputs> {
  const [m, s, c, cm, h] = await Promise.all([
    api
      .fetchFieldLayerList(f.field_id)
      .then((listed) =>
        listed.document ? layersFromDocument(listed.document) : [],
      )
      .catch(() => []),
    api.fetchSoilSamples(f.field_id).catch(() => []),
    api.fetchFieldCovariates(f.field_id).catch(() => []),
    api.fetchSoilComposition(f.field_id).catch(() => null),
    api
      .fetchHaneyPoints(f.field_id)
      .then((hp) => hp.map(haneyFromWire))
      .catch(() => loadLocalHaney(f.field_id)),
  ]);
  return {
    field: f,
    manifest: m,
    samples: s,
    covs: c,
    comp: cm,
    haneyPoints: h,
  };
}

export interface PresetRunResult {
  ok: boolean;
  error?: string;
  skipped: string[]; // preset flags dropped because their data wasn't available
}

type FertSummary = ReturnType<typeof assembleFertLayers>["summary"];

// The result of computing a preset's Rx layers WITHOUT saving (the compute artifacts the save step needs).
export interface PresetLayers {
  ok: boolean;
  error?: string;
  skipped: string[]; // preset flags dropped because their data wasn't available
  layers: ManifestLayer[]; // the per-cell Rx surfaces (fert-<nu>, fert-<nu>-total, fert-mg)
  summary: FertSummary | null;
  soil: RecInputs | null;
  sources: Record<string, string>;
  cellCount: number;
  gridId: string;
  cellOrder: "grid";
  geoms: { type: string; coordinates: unknown }[];
  centroids: { lng: number; lat: number }[];
  /** Covariate options /rx-inputs did not have. The control is disabled with `message`. */
  disabled: DisabledCovariateToggle[];
  yieldMaps: YieldMaps;
  withCation: boolean;
  applyReplP: boolean;
  applyReplK: boolean;
  replacement: Replacement | null;
  /* * the K rotation this run resolved (crop + basis per season) — surfaced so a caller can label it */
  kPlan?: SeasonPlan[];
}

// Compute the per-cell Rx LAYERS for one preset WITHOUT saving or touching any store — the pure
// compute path shared by `computeAndSavePresetRec` (which then saves) and the AutoRx preview cards
// (which just render the `fert-<nu>` surface). Applies each preset flag ONLY when its inputs are
// available (skipped flags reported).
export async function computePresetLayers(
  inputs: PresetInputs,
  preset: FertPreset,
  _seasons: Season[] = [],
): Promise<PresetLayers> {
  const { field, manifest, samples, covs, comp, haneyPoints } = inputs;
  const skipped: string[] = [];
  const fieldId = field.field_id;
  const fail = (error: string): PresetLayers => ({
    ok: false,
    error,
    skipped,
    layers: [],
    summary: null,
    soil: null,
    sources: {},
    cellCount: 0,
    gridId: "",
    cellOrder: "grid",
    geoms: [],
    centroids: [],
    disabled: [],
    yieldMaps: { machine: {}, simulated: {} },
    withCation: false,
    applyReplP: false,
    applyReplK: false,
    replacement: null,
  });

  // which modeled props exist on this field
  const availProp = new Set<string>();
  for (const p of BASE_PROPS)
    if (import.meta.env.MODE === "test" || candidatesFor(p, manifest).length)
      availProp.add(p);

  // the nutrient itself must be mappable
  const nu = preset.nutrient;
  if (!(NUTRIENT_NEEDS[nu] ?? []).every((p) => availProp.has(p))) {
    const missing = (NUTRIENT_NEEDS[nu] ?? []).filter((p) => !availProp.has(p));
    return fail(
      `Map ${missing.join(" + ").toUpperCase()} first (run Map Soil) — this Rx needs ${missing.join(", ")}.`,
    );
  }

  // Cells are the stored field grid. Covariate columns are /rx-inputs, already
  // in that order. A missing column disables its toggle; it is not read from
  // the stack by row position.
  const f = preset.flags;
  const cells = await loadRxCells(fieldId);
  const wantsCaMg = !!(f.cation || f.limeAlbrecht || f.k2oAlbrecht);
  const optionKeys: string[] = [];
  if (f.useDiffusion && nu === "p2o5") optionKeys.push(DIFF_P);
  if (f.useDiffusion && nu === "k2o") optionKeys.push(DIFF_K);
  if (f.kFix) optionKeys.push(MAP_UNIT, CLAY);
  if (wantsCaMg) optionKeys.push(IDW_CA, IDW_MG);
  const applyReplP = !!f.replP && nu === "p2o5";
  const applyReplK = !!f.replK && nu === "k2o";
  if (applyReplP || applyReplK) optionKeys.push(...yieldOptionKeys(covs));
  const join = joinForGrid(
    cells,
    await loadRxInputs(fieldId, optionKeys),
    optionKeys,
  );
  const disabled: DisabledCovariateToggle[] = [];
  const noteDisabled = (toggle: DisabledCovariateToggle | null) => {
    if (!toggle) return;
    disabled.push(toggle);
    skipped.push(toggle.message);
  };

  // When the diffusion K is used it SUPERSEDES the BCSR cation step — cation balance is built into
  // the diffusion K (Schofield AR uses Ca/Mg), so don't double-count. In the fallback (diffusion K
  // not built) cation applies as before.
  const diffP =
    f.useDiffusion && nu === "p2o5"
      ? gridNumbers(join, DIFF_P, cells.cellCount)
      : null;
  const diffK =
    f.useDiffusion && nu === "k2o"
      ? gridNumbers(join, DIFF_K, cells.cellCount)
      : null;
  const useDiffP = !!diffP;
  const useDiffK = !!diffK;
  if (f.useDiffusion && nu === "p2o5" && !useDiffP)
    noteDisabled(covariateToggle(DIFF_P, false));
  if (f.useDiffusion && nu === "k2o" && !useDiffK)
    noteDisabled(covariateToggle(DIFF_K, false));

  const caPts = nutrientPts(samples, "ca");
  const mgPts = nutrientPts(samples, "mg");
  const cationOk = cationInputsAvailable(availProp, caPts.length, mgPts.length);
  const kFixOk = !!comp && clayGroupsInField(comp).length > 0;
  const haneyKeys = new Set(haneyParamsWithData(haneyPoints).map((p) => p.key));
  const pMinOk =
    haneyKeys.has("organic_p") &&
    haneyKeys.has("weoc_weon") &&
    haneyKeys.has("co2c");

  const withCation = !!f.cation && cationOk && !useDiffK; // OFF when diffusion K is used (built into that layer)
  if (f.cation && !cationOk && !useDiffK)
    skipped.push("cation balance (needs ≥4 Ca & Mg samples + K + CEC maps)");
  const withK2oAlb =
    !!f.k2oAlbrecht && availProp.has("k") && availProp.has("cec");
  const withLimeAlb = !!f.limeAlbrecht && cationOk;
  if (f.limeAlbrecht && !withLimeAlb)
    skipped.push("Albrecht lime basis (needs Ca + Mg + K + CEC)");
  const mapUnit = f.kFix ? gridValues(join, MAP_UNIT, cells.cellCount) : null;
  if (f.kFix && !mapUnit) noteDisabled(covariateToggle(MAP_UNIT, false));
  const withKFix = !!f.kFix && !!mapUnit && kFixOk;
  if (f.kFix && mapUnit && !kFixOk)
    skipped.push("K-fixation buildup (needs clay-mineralogy data)");
  const withPMin = !!f.pMin && pMinOk;
  if (f.pMin && !withPMin)
    skipped.push(
      "P-mineralization credit (needs Haney organic P + C:N + CO₂ data)",
    );

  // props to sample from the modeled maps
  const needed = new Set<string>(NUTRIENT_NEEDS[nu]);
  if (withCation || withLimeAlb) ["k", "cec"].forEach((p) => needed.add(p));

  const sources: Record<string, string> = {};
  for (const p of needed) {
    const c = candidatesFor(p, manifest);
    if (c[0]) sources[p] = c[0].id;
  }
  // Optional. Only an existing ml-bph map — never a kriged sample buffer pH.
  const sampleProps = [...needed];
  if (nu === "lime") {
    const bphLayer = candidatesFor("bph", manifest)[0];
    if (bphLayer) {
      sources.bph = bphLayer.id;
      sampleProps.push("bph");
    }
  }
  const soil = await sampleSoil(
    fieldId,
    cells.centroids,
    sampleProps,
    sources,
    manifest,
  );

  if (useDiffP && diffP) {
    soil.pRaw = soil.p;
    soil.p = diffP;
  }
  if (useDiffK && diffK) {
    soil.kRaw = soil.k;
    soil.k = diffK;
  }

  // Prefer the served columns. A grid without them gets the same IDW (power 2)
  // on the samples, and the skip notice says so. That fallback is not a disabled toggle.
  if (withCation || withLimeAlb) {
    const ca = gridNumbers(join, IDW_CA, cells.cellCount);
    const mg = gridNumbers(join, IDW_MG, cells.cellCount);
    soil.ca = ca ?? idwAnalyte(caPts, cells.centroids);
    soil.mg = mg ?? idwAnalyte(mgPts, cells.centroids);
    if (!ca || !mg) {
      skipped.push(
        `served Ca/Mg IDW layers are not on this grid; the current manifest ` +
          `is ${LATEST_MANIFEST}. Interpolated in-browser by the SAME IDW instead; rebuild the covariates to use the served layers.`,
      );
    }
  }
  if (withK2oAlb) soil.k2oAlbrecht = true;
  if (withLimeAlb) soil.limeAlbrecht = true;
  if (withK2oAlb || withLimeAlb)
    soil.albrechtTargets = { ...DEFAULT_ALBRECHT_TARGETS };

  if (withKFix && mapUnit && comp) {
    const clay = gridValues(join, CLAY, cells.cellCount);
    const eff = kFixEfficiencyOnGrid(comp, mapUnit, clay, "standard", 0.5);
    if (eff.length === cells.cellCount) soil.kFixEff = eff;
  }
  if (withPMin) {
    const credit = perCellPMinCredit(haneyPoints, cells.centroids);
    if (credit.length === cells.cellCount) soil.pMinCredit = credit;
  }

  // crop-removal replacement (summed across seasons, per-crop coefficients)
  const replacement: Replacement | null = null;
  // Same resolver and builder Customize uses, so a card and its Customize cannot disagree - which
  // is exactly how they drifted before.
  let kPlan: SeasonPlan[] = [];
  let yieldMaps: YieldMaps = { machine: {}, simulated: {} };
  if (applyReplK || applyReplP) {
    const yieldRead = yieldFromJoin(covs, join, cells.cellCount);
    yieldMaps = yieldRead.maps;
    for (const toggle of yieldRead.disabled) noteDisabled(toggle);
    kPlan = resolveSeasons(
      DEFAULT_SEASON_CROPS,
      yieldMaps,
      nu === "p2o5" ? "p2o5" : "k2o",
    );
    const repl = replacementForSeasons(kPlan, yieldMaps, cells.cellCount, {
      nutrient: nu === "p2o5" ? "p2o5" : "k2o",
    });
    if (repl) {
      if (applyReplK) soil.kMaintenance = repl.k2o;
      if (applyReplP) soil.pMaintenance = repl.p2o5;
    }
  }

  // the preset's own rate window (Genius only today) — so a blind card click produces the same bounded
  // rate the Customize panel opens on
  if (preset.rateBounds) {
    if (nu === "p2o5") {
      soil.pMinLbAc = preset.rateBounds.min;
      soil.pMaxLbAc = preset.rateBounds.max;
      if (preset.rateBounds.dropBelow != null)
        soil.pDropBelowLbAc = preset.rateBounds.dropBelow;
    }
    if (nu === "k2o") {
      soil.kMinLbAc = preset.rateBounds.min;
      soil.kMaxLbAc = preset.rateBounds.max;
      if (preset.rateBounds.dropBelow != null)
        soil.kDropBelowLbAc = preset.rateBounds.dropBelow;
    }
  }
  soil.rateWindow = "final";

  const opts = {
    nutrients: [nu],
    cation: withCation,
    replacement,
    // BOTH maintenance arrays ride the soil now; passing them here too would double-count them AND rebuild
    // the separate total layers that escape the clamps
    replP: false,
    replK: false,
    rateWindow: "final" as const,
    ...(withCation ? { cationRatios: { ...DEFAULT_CATION_RATIOS } } : {}),
  };
  const { layers, summary } = assembleFertLayers(cells.geoms, soil, opts);
  if (
    !layers.some(
      (l) =>
        ((l.inline as { features?: unknown[] })?.features?.length ?? 0) > 0,
    )
  ) {
    return fail("The modeled maps had no values on this field’s cells.");
  }
  return {
    ok: true,
    skipped,
    layers,
    summary,
    soil,
    sources,
    ...gridStamp(cells.gridId, cells.cellCount),
    geoms: cells.geoms,
    centroids: cells.centroids,
    disabled,
    yieldMaps,
    withCation,
    applyReplP,
    applyReplK,
    replacement,
    kPlan,
  };
}

// Compute + save the rec for ONE preset. Runs the shared compute, then persists (localStorage + live map +
// the fert-recs store). `seasons` drives crop-removal replacement.
export async function computeAndSavePresetRec(
  inputs: PresetInputs,
  preset: FertPreset,
  seasons: Season[],
  name: string,
): Promise<PresetRunResult> {
  const fieldId = inputs.field.field_id;
  const nu = preset.nutrient;
  useFertRecs.getState().computing(fieldId);
  try {
    const c = await computePresetLayers(inputs, preset, seasons);
    if (!c.ok || !c.soil || !c.summary) {
      useFertRecs.getState().fail(fieldId, c.error ?? "Something went wrong.");
      return { ok: false, error: c.error ?? "error", skipped: c.skipped };
    }
    const soil = nu === "lime" ? withLimeSoilStamp(c.soil, nu) : c.soil;
    // years this rec stays current for the Action Flags: Lime = 4 years; P/K = the number of
    // crop-removal seasons (build-only = 1).
    const coversThroughYear =
      new Date().getFullYear() +
      (nu === "lime" ? 4 : Math.max(1, seasons.length)) -
      1;
    const saved: SavedRecSet = {
      id: `frs-${new Date().getTime()}`,
      name: name.trim() || `${preset.label} ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      fieldId,
      ...gridStamp(c.gridId, c.cellCount),
      nutrients: [nu],
      coversThroughYear,
      cation: c.withCation,
      ...(c.withCation
        ? {
            cationCaMg: DEFAULT_CATION_RATIOS.caMg,
            cationMgK: DEFAULT_CATION_RATIOS.mgK,
          }
        : {}),
      sources: c.sources,
      soil,
      replacement: c.replacement,
      ...(c.replacement ? { replP: c.applyReplP, replK: c.applyReplK } : {}),
      kfix: !!soil.kFixEff,
      ...(soil.kFixEff
        ? { kfixSet: "standard" as const, kfixRelease: 0.5 }
        : {}),
      pMin: !!soil.pMinCredit?.some(
        (v) => v != null && Number.isFinite(v) && v > 0,
      ),
      ...(soil.k2oAlbrecht ? { albrechtK: true } : {}),
      ...(soil.limeAlbrecht ? { albrechtLime: true } : {}),
      ...(soil.albrechtTargets
        ? { albrechtTargets: soil.albrechtTargets }
        : {}),
      rateWindow: "final",
    };
    // The server has to accept the set before it is a saved Rx. A rejection
    // is returned to the caller and is not written locally.
    const savedSets = await commitRecSet(saved);
    useLayerStore.getState().reseed(c.layers);
    useFertRecs
      .getState()
      .ready(fieldId, c.layers, c.summary, saved.id, savedSets);
    // NOTE: navigation to the map is left to the caller so a "some options were skipped" notice can be
    // shown first (Build Rx presets surface `skipped`).
    return { ok: true, skipped: c.skipped };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Something went wrong.";
    useFertRecs.getState().fail(fieldId, msg);
    return { ok: false, error: msg, skipped: [] };
  }
}

// default seasons for the pop-up: one season of the given crop at its default yield
export function defaultSeason(crop = "corn"): Season {
  return { crop, yieldLevel: DEFAULT_YIELD[crop] ?? 200 };
}

// Crop removal is imported with this module, so the list is filled before the
// season popup renders.
export const PRESET_CROPS = Object.keys(CROP_REMOVAL);

/*
 *
 * Fetch both variable-rate yield bases for the crops we can offer, once.
 *
 * Machine years are averaged per crop (the grower's own history); simulated is already an all-years
 * average per crop by construction, resolved by SCANNING the catalog rather than by building an exact key
 * (compute serves plural crop words — see `simYieldKeysByCrop`).
 *
 * Never throws: a basis that fails to load is simply absent, and the callers disclose it. Lives here
 * rather than in the editor store because the preset run, the preview card and the editor all need it and
 * must agree.
 */
type YieldCov = {
  key: string;
  category?: string;
  has_values?: boolean;
  year?: number | null;
};

export function yieldOptionKeys(covs: YieldCov[]): string[] {
  return [
    ...Object.values(simYieldKeysByCrop(covs)),
    ...yieldOptionsFromCovariates(covs).map((o) => o.key),
  ];
}

/** Yield columns from an /rx-inputs join. A missing column disables that basis. */
export function yieldFromJoin(
  covs: YieldCov[],
  join: RxJoin,
  cellCount: number,
): { maps: YieldMaps; disabled: DisabledCovariateToggle[] } {
  const maps: YieldMaps = { machine: {}, simulated: {} };
  const disabled: DisabledCovariateToggle[] = [];
  const simKeys = Object.entries(simYieldKeysByCrop(covs));
  const machOpts = yieldOptionsFromCovariates(covs);
  for (const [crop, key] of simKeys) {
    const col = gridNumbers(join, key, cellCount);
    if (col) maps.simulated[crop] = col;
    else {
      const toggle = covariateToggle(key, false, { basis: "simulated", crop });
      if (toggle) disabled.push(toggle);
    }
  }
  for (const crop of new Set(machOpts.map((o) => o.crop))) {
    const opts = machOpts.filter((o) => o.crop === crop);
    const present = opts
      .map((o) => gridNumbers(join, o.key, cellCount))
      .filter((col): col is (number | null)[] => !!col);
    if (present.length) maps.machine[crop] = meanYieldMaps(present);
    else if (opts[0]) {
      const toggle = covariateToggle(opts[0].key, false, {
        basis: "machine",
        crop,
      });
      if (toggle) disabled.push(toggle);
    }
  }
  return { maps, disabled };
}

export async function loadYieldMaps(
  fieldId: string,
  covs: YieldCov[],
  cellCount: number,
): Promise<YieldMaps> {
  const keys = yieldOptionKeys(covs);
  if (!keys.length) return { machine: {}, simulated: {} };
  try {
    const cells = await loadRxCells(fieldId);
    const join = joinForGrid(cells, await loadRxInputs(fieldId, keys), keys);
    return yieldFromJoin(covs, join, cellCount).maps;
  } catch {
    // absent bases are disclosed by the caller, never a silent zero removal
    return { machine: {}, simulated: {} };
  }
}
